import { Stack, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ConfirmationDialog } from '@components/ConfirmationDialog';

import type { OrgFeatureChangeFieldsFragment } from 'generated/custom-plan.generated';

const messages = defineMessages({
  title: {
    id: 'orgSettings.licensePlan.featureRemoval.title',
    defaultMessage:
      'Disable {count, plural, one {this feature} other {these features}}?',
  },
  intro: {
    id: 'orgSettings.licensePlan.featureRemoval.intro',
    defaultMessage:
      'Saving removes {count, plural, one {this patient feature} other {these patient features}} from every patient plan that includes {count, plural, one {it} other {them}}.',
  },
  affected: {
    id: 'orgSettings.licensePlan.featureRemoval.affected',
    defaultMessage: 'Removes {feature} from: {plans}',
  },
  revoke: {
    id: 'orgSettings.licensePlan.featureRemoval.revoke',
    defaultMessage:
      'Any patient using {count, plural, one {this feature} other {these features}} loses access immediately. This is a live access change, not only a configuration change.',
  },
  confirm: {
    id: 'GLOBAL.confirm',
    defaultMessage: 'Confirm',
  },
  cancel: {
    id: 'GLOBAL.cancel',
    defaultMessage: 'Cancel',
  },
});

interface FeatureChangeDialogProps {
  /**
   * Features about to be disabled that are still used by at least one plan. An
   * empty array keeps the dialog closed — the caller saves without asking.
   */
  changes: OrgFeatureChangeFieldsFragment[];
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

/**
 * Informational gate before a feature-disabling save: it names the features
 * going away, the plans that lose them, and that live patient access is
 * revoked. Confirm or Cancel only. Driven by `previewOrgFeatureChange`, so the
 * copy always matches the mutation.
 */
export default function FeatureChangeDialog({
  changes,
  onConfirm,
  onCancel,
}: FeatureChangeDialogProps) {
  const { formatMessage } = useIntl();
  const count = changes.length;

  return (
    <ConfirmationDialog
      cancelText={formatMessage(messages.cancel)}
      confirmText={formatMessage(messages.confirm)}
      content={
        <Stack data-testid="feature-change-dialog" gap={1.5}>
          <Typography variant="body2">
            {formatMessage(messages.intro, { count })}
          </Typography>

          <Stack component="ul" gap={0.5} sx={{ pl: 2.5, m: 0 }}>
            {changes.map((change) => (
              <Typography
                component="li"
                data-testid={`feature-removal-${change.feature_id}`}
                key={change.feature_id}
                variant="body2"
              >
                {formatMessage(messages.affected, {
                  feature: <strong>{change.display_name}</strong>,
                  plans: change.affected_plans
                    .map(({ name }) => name)
                    .join(', '),
                })}
              </Typography>
            ))}
          </Stack>

          <Typography color="error" variant="body2">
            {formatMessage(messages.revoke, { count })}
          </Typography>
        </Stack>
      }
      onCancel={onCancel}
      // ConfirmationDialog routes Escape and backdrop clicks through its own
      // handleClose and does not honour `persistent`. Without this the caller's
      // pending confirmation never settles and the save hangs.
      onClose={onCancel}
      onConfirm={onConfirm}
      open={count > 0}
      persistent
      title={formatMessage(messages.title, { count })}
      variant="warning"
    >
      {() => null}
    </ConfirmationDialog>
  );
}
